/**
 * CMS Constants - Configuration for CMS lazy loading behavior
 */

/**
 * Default tab configuration
 */
export const CMS_DEFAULTS = {
  // Default tab slug to load initially (case-insensitive match)
  DEFAULT_TAB_SLUG: 'overview',

  // Default tab title (used for display if slug doesn't match)
  DEFAULT_TAB_TITLE: 'Overview',

  // If "overview" tab doesn't exist, fallback to first tab
  FALLBACK_TO_FIRST_TAB: true,
};

/**
 * Cache configuration
 */
export const CMS_CACHE = {
  // Time-to-live for tab content cache (in milliseconds)
  // Set to 0 to disable TTL (cache forever until cleared)
  TAB_CONTENT_TTL: 0,

  // Time-to-live for product structure cache (in milliseconds)
  STRUCTURE_TTL: 0,
};

/**
 * Get the default tab slug from a list of tabs
 * Prioritizes "overview" tab, falls back to first tab if configured
 *
 * @param {Array<{slug?: string, title?: string}>} tabs - Array of tab metadata
 * @returns {string|null} The default tab slug or null if no tabs
 */
export const getDefaultTabSlug = (tabs) => {
  if (!tabs || tabs.length === 0) {
    return null;
  }

  // 1. Try to find "overview" tab (case-insensitive)
  const overviewTab = tabs.find(
    (t) => t.slug?.toLowerCase() === CMS_DEFAULTS.DEFAULT_TAB_SLUG.toLowerCase()
  );
  if (overviewTab?.slug) {
    return overviewTab.slug;
  }

  // 2. Fallback to first tab if configured
  if (CMS_DEFAULTS.FALLBACK_TO_FIRST_TAB) {
    return tabs[0]?.slug || null;
  }

  return null;
};

/**
 * Find tab index by slug
 *
 * @param {Array<{slug?: string}>} tabs - Array of tab metadata
 * @param {string} tabSlug - Tab slug to find
 * @returns {number} Tab index or 0 if not found
 */
export const findTabIndexBySlug = (tabs, tabSlug) => {
  if (!tabs || !tabSlug) {
    return 0;
  }
  const index = tabs.findIndex((t) => t.slug === tabSlug);
  return index >= 0 ? index : 0;
};

/**
 * Get tab by slug
 *
 * @param {Array<{slug?: string}>} tabs - Array of tab metadata
 * @param {string} tabSlug - Tab slug to find
 * @returns {Object|null} Tab object or null if not found
 */
export const getTabBySlug = (tabs, tabSlug) => {
  if (!tabs || !tabSlug) {
    return null;
  }
  return tabs.find((t) => t.slug === tabSlug) || null;
};
